/**
 * Add Approved Supplier modal (Suppliers page → Add menu).
 *
 * Creates a supplier that is immediately approved (skips onboarding) and links
 * it to one or more customer sites.
 *
 * CR-6654: the supplier is only visible to whoever it is linked to, so the
 * modal names the customer target — resolved by the page from the active view
 * — and submits exactly what it showed, so where the supplier lands can't drift
 * from what the user was told.
 *
 * CR-6680: at group scope the target is ambiguous, so the user picks. That pick
 * is now a tile-based multi-select — one tile per site plus a "Select All" tile
 * — so a supplier relevant to several sites (e.g. a material supplier onboarded
 * across every site) is added to all of them in one pass, instead of repeating
 * the wizard per site. The backend already links one relationship per id in
 * ``customer_ids``.
 *
 * Its own component because Suppliers.tsx is already over the 500-line limit.
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  AlertCircle, Building2, CheckSquare, Loader2, Square,
} from 'lucide-react';
import { toast } from 'sonner';
import { suppliersAPI, apiErrorMessage, ExistingSupplierMatch } from '../../lib/api';
import { AddTarget } from './types';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { COUNTRY_OPTIONS } from '../../lib/countries';
import SupplierNameSearch from './SupplierNameSearch';
import CustomerSiteSettings, { SiteSetting } from './CustomerSiteSettings';

interface AddApprovedSupplierModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Where the supplier will be linked, resolved by the page from the active
   * view. ``options`` (group scope) means the user must pick; otherwise the
   * resolved ``ids`` are used as-is.
   */
  addTarget: AddTarget | null;
  /** True while the page is resolving the target — block submit until it lands. */
  addTargetLoading: boolean;
  /** Refresh the directory so the new supplier is visible. */
  onCreated: () => void;
  /**
   * CR-8252: compliance is on, so a new supplier is onboarded, not added. The
   * modal then only links a supplier another site has already approved (the
   * "already approved at another site" step).
   */
  reuseOnly?: boolean;
}

const INITIAL_FORM = {
  company_name: '', registration_number: '',
  primary_email: '', primary_phone: '', website: '',
  address_line_1: '', address_line_2: '', city: '', state: '', postal_code: '', country: '',
  payment_terms: '', annual_revenue: '', employee_count: '', year_established: '',
  description: '',
};

const CURRENCIES = ['GBP', 'USD', 'EUR', 'PLN', 'CAD', 'AUD', 'CHF', 'SEK', 'NOK', 'DKK', 'CZK', 'JPY', 'CNY', 'INR'];

export default function AddApprovedSupplierModal({
  open, onOpenChange, addTarget, addTargetLoading, onCreated, reuseOnly = false,
}: AddApprovedSupplierModalProps) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  // The sites chosen at group scope, where the target is ambiguous.
  const [pickedCustomerIds, setPickedCustomerIds] = useState<number[]>([]);
  // CR-6685: the existing supplier picked from the name typeahead, if any. When
  // set, submit links this supplier instead of creating a new company, and its
  // populated fields are shown read-only.
  const [existingSupplierId, setExistingSupplierId] = useState<number | null>(null);
  // The picked supplier's original record — used to lock only the fields it
  // already has a value for, leaving empty ones editable so they can be filled.
  const [selectedMatch, setSelectedMatch] = useState<ExistingSupplierMatch | null>(null);
  // CR-6366 alignment: per-site supplier code + currency, keyed by customer id.
  // Entries persist across tile toggles so re-selecting a site restores what was
  // typed; submit sends only the currently-selected sites.
  const [customerSettings, setCustomerSettings] = useState<Record<number, SiteSetting>>({});

  // Reset everything each time the modal opens — a stale selection or a
  // half-filled form must not carry over between opens.
  useEffect(() => {
    if (open) {
      setPickedCustomerIds([]);
      setForm(INITIAL_FORM);
      setExistingSupplierId(null);
      setSelectedMatch(null);
      setCustomerSettings({});
    }
  }, [open]);

  const options = addTarget?.options;
  const allIds = options?.map((o) => o.id) ?? [];
  const allSelected = allIds.length > 0 && allIds.every((id) => pickedCustomerIds.includes(id));

  // What will actually be written: the picked sites at group scope, or the
  // resolved target otherwise. Single source of truth for the submit guard and
  // the request body.
  const effectiveCustomerIds: number[] = options ? pickedCustomerIds : (addTarget?.ids || []);

  const toggleAll = () => setPickedCustomerIds(allSelected ? [] : allIds);
  const toggleOne = (id: number) =>
    setPickedCustomerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  // The sites whose code/currency the buyer should fill: the picked tiles at
  // group scope, or the resolved target otherwise.
  const selectedSites: { id: number; name: string }[] = options
    ? options.filter((o) => pickedCustomerIds.includes(o.id))
    : (addTarget?.ids || []).map((id) => ({ id, name: addTarget?.label || `Site #${id}` }));

  const updateSiteSetting = (id: number, patch: Partial<SiteSetting>) =>
    setCustomerSettings((prev) => {
      const cur = prev[id] || { supplier_code: '', currency: 'GBP' };
      return { ...prev, [id]: { ...cur, ...patch } };
    });

  // CR-6685: pick an existing supplier from the typeahead — populate the form
  // from its record and remember its id so submit links rather than duplicates.
  const selectExisting = (m: ExistingSupplierMatch) => {
    setForm({
      company_name: m.company_name,
      registration_number: m.registration_number,
      primary_email: m.primary_email,
      primary_phone: m.primary_phone,
      website: m.website,
      address_line_1: m.address_line_1,
      address_line_2: m.address_line_2,
      city: m.city,
      state: m.state,
      postal_code: m.postal_code,
      country: m.country,
      payment_terms: m.payment_terms,
      annual_revenue: m.annual_revenue,
      employee_count: m.employee_count,
      year_established: m.year_established,
      description: '',
    });
    setExistingSupplierId(m.supplier_id);
    setSelectedMatch(m);
  };

  // Typing a name abandons any existing selection and starts a fresh
  // new-supplier form so the previous supplier's details don't linger.
  const handleNameChange = (name: string) => {
    if (existingSupplierId) {
      setForm({ ...INITIAL_FORM, company_name: name });
      setExistingSupplierId(null);
      setSelectedMatch(null);
    } else {
      setForm((f) => ({ ...f, company_name: name }));
    }
  };

  const clearExisting = () => {
    setForm(INITIAL_FORM);
    setExistingSupplierId(null);
    setSelectedMatch(null);
  };

  // CR-6685: with an existing supplier picked, lock the fields it already has a
  // value for; empty fields stay editable so the user can fill in (backfill)
  // missing details, which are then saved to the supplier on submit.
  const lockedField = (key: keyof ExistingSupplierMatch) =>
    existingSupplierId !== null && !!(selectedMatch && selectedMatch[key]);

  const handleSubmit = async () => {
    if (!form.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    // CR-7774: City / Town is mandatory. When an existing supplier is picked the
    // field is pre-filled from its record, so this only stops a genuinely
    // city-less supplier — new, or a legacy one that never had one.
    if (!form.city.trim()) {
      toast.error('City / Town is required');
      return;
    }
    // Submit the target the modal displayed, not a freshly resolved one, so the
    // supplier cannot land somewhere other than where the user was told.
    const customerIds = effectiveCustomerIds;
    if (!customerIds.length) {
      toast.error(
        options
          ? 'Select at least one customer site to add this supplier to.'
          : 'Select a site or company in the view switcher before adding a supplier.'
      );
      return;
    }
    setSaving(true);
    try {
      await suppliersAPI.createFromWizard({
        company_name: form.company_name.trim(),
        // CR-6681: send every field the modal collects, and the address as its
        // individual parts — the old single concatenated ``company_address``
        // landed the whole address in Address Line 1 and dropped the rest.
        registration_number: form.registration_number.trim(),
        primary_email: form.primary_email.trim(),
        primary_phone: form.primary_phone.trim(),
        website: form.website.trim(),
        payment_terms: form.payment_terms.trim(),
        annual_revenue: form.annual_revenue.trim(),
        employee_count: form.employee_count.trim(),
        year_established: form.year_established.trim(),
        address_line_1: form.address_line_1.trim(),
        address_line_2: form.address_line_2.trim(),
        address_city: form.city.trim(),
        address_region: form.state.trim(),
        address_postcode: form.postal_code.trim(),
        address_country: form.country.trim(),
        // Profile default currency (supplier-level) seeds from the primary
        // site; each site's own trading currency is in customer_settings.
        currency: (customerSettings[customerIds[0]]?.currency) || 'GBP',
        description: form.description.trim(),
        customer_id: customerIds[0],
        customer_ids: customerIds,
        // CR-6366 alignment: per-customer supplier code + currency written to
        // each site's relationship. Only the currently-selected sites are sent.
        customer_settings: Object.fromEntries(customerIds.map((id) => {
          const s = customerSettings[id] || { supplier_code: '', currency: 'GBP' };
          return [String(id), { supplier_code: s.supplier_code.trim(), currency: s.currency }];
        })),
        // CR-6654: this modal is "Add Approved Supplier" — create it already
        // approved so it lands in the approved directory, not as a draft.
        approved: true,
        // CR-6685: link the picked existing supplier instead of duplicating.
        existing_supplier_id: existingSupplierId ?? undefined,
      });
      const siteNote = customerIds.length > 1 ? ` (${customerIds.length} sites)` : '';
      const verb = existingSupplierId ? 'linked' : 'added';
      toast.success(`Supplier "${form.company_name.trim()}" ${verb} successfully${siteNote}`);
      onOpenChange(false);
      setForm(INITIAL_FORM);
      onCreated();
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'Failed to add supplier'));
    } finally {
      setSaving(false);
    }
  };

  const tileClass = (selected: boolean) =>
    `flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
      selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-input hover:bg-muted'
    }`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          {/* CR-8252 (Aaron, 27 Oct): without compliance there is no approval to speak of, so it is just "Add Supplier". */}
          <DialogTitle>{reuseOnly ? 'Add supplier approved at another site' : 'Add Supplier'}</DialogTitle>
          <DialogDescription>
            {reuseOnly
              ? 'Pick a supplier another site has already approved. It is linked to the selected site(s) without going through onboarding again.'
              : 'Add a new supplier with company details.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-2">
          {/* CR-6654: name the customer this supplier will be linked to. The
              supplier is only visible to whoever it is linked to, so adding
              from the wrong view scope silently produced an invisible
              supplier. */}
          {addTargetLoading ? (
            <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Resolving customer…
            </div>
          ) : options ? (
            /* CR-6680: group scope names no single site, so the buyer picks one
               or more — tiles toggle select/deselect, "Select All" toggles the
               lot. A supplier can serve several sites at once. */
            <div className="space-y-2 rounded-md border bg-muted/40 px-3 py-2.5">
              <Label className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                Add to customer *
                {pickedCustomerIds.length > 0 && (
                  <span className="ml-1 font-normal text-muted-foreground">
                    {pickedCustomerIds.length} selected
                  </span>
                )}
              </Label>
              <p className="text-xs text-muted-foreground">
                Select one or more sites — the supplier is added to every site you choose.
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <button type="button" onClick={toggleAll} className={tileClass(allSelected)}>
                  <span className="flex min-w-0 items-center gap-2">
                    {allSelected
                      ? <CheckSquare className="h-4 w-4 shrink-0 text-primary" />
                      : <Square className="h-4 w-4 shrink-0 text-muted-foreground" />}
                    <span className="truncate font-medium">Select All</span>
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {allIds.length} site{allIds.length === 1 ? '' : 's'}
                  </span>
                </button>
                {options.map((o) => {
                  const selected = pickedCustomerIds.includes(o.id);
                  return (
                    <button key={o.id} type="button" onClick={() => toggleOne(o.id)} className={tileClass(selected)}>
                      <span className="flex min-w-0 items-center gap-2">
                        {selected
                          ? <CheckSquare className="h-4 w-4 shrink-0 text-primary" />
                          : <Square className="h-4 w-4 shrink-0 text-muted-foreground" />}
                        <span className="truncate">{o.name}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : addTarget?.ids.length ? (
            <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
              <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">Adding to</span>
              <span className="font-medium">{addTarget.label}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertCircle className="h-4 w-4 shrink-0" />
              Select a site or company in the view switcher before adding a supplier.
            </div>
          )}
          {/* CR-6366 alignment: each chosen site gets its own supplier code +
              trading currency, written to that site's relationship. */}
          <CustomerSiteSettings
            sites={selectedSites}
            settings={customerSettings}
            onChange={updateSiteSetting}
            currencies={CURRENCIES}
          />
          {/* CR-6685: Company Name is a typeahead over existing suppliers — pick
              a match to reuse and link it (no duplicate); free text adds a new
              supplier as before. */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Supplier Identification</h4>
            <div className="space-y-1.5">
              <Label>Company Name <span className="text-red-500">*</span></Label>
              <SupplierNameSearch
                value={form.company_name}
                onChange={handleNameChange}
                onSelectExisting={selectExisting}
                placeholder="Start typing to find an existing supplier…"
                // CR-8252: reuse only adds a supplier already approved; nothing new is created here.
                noMatchText={reuseOnly
                  ? 'No approved supplier found in your organisation. A new supplier goes through Onboard New Supplier.'
                  : undefined}
              />
              <p className="text-xs text-muted-foreground">
                {reuseOnly
                  ? 'We search existing suppliers as you type. Pick one that is already approved. A new supplier goes through Onboard New Supplier.'
                  : 'We search existing suppliers as you type — pick one to reuse and link it, or keep typing to add a new supplier.'}
              </p>
            </div>
            {existingSupplierId && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
                <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div className="flex-1">
                  <p className="font-medium">Using an existing supplier</p>
                  <p className="text-xs text-muted-foreground">
                    It will be linked to the selected site(s) — no duplicate is created. Its filled-in details are read-only; you can complete any empty fields and they'll be saved to the supplier.
                  </p>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={clearExisting}>Change</Button>
              </div>
            )}
          </div>
          {/* CR-6685: when an existing supplier is picked, each field is locked
              only if that supplier already has a value for it — empty fields stay
              editable so the user can backfill missing details. */}
          <div className="space-y-6">
            {/* Company Number (supplier code is per-site, above) */}
            <div className="space-y-1.5">
              <Label>Company Number</Label>
              <Input value={form.registration_number} onChange={(e) => setForm({ ...form, registration_number: e.target.value })} placeholder="e.g. 12345678" disabled={lockedField('registration_number')} />
            </div>
          {/* Payment & Financial */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Payment & Financial</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Payment Terms</Label>
                <Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="e.g. Net 30" disabled={lockedField('payment_terms')} />
              </div>
              <div className="space-y-1.5">
                <Label>Annual Revenue</Label>
                <Input value={form.annual_revenue} onChange={(e) => setForm({ ...form, annual_revenue: e.target.value })} placeholder="e.g. 5000000" disabled={lockedField('annual_revenue')} />
              </div>
            </div>
          </div>
          {/* Business Details */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Business Details</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Employee Count</Label>
                <Input type="number" value={form.employee_count} onChange={(e) => setForm({ ...form, employee_count: e.target.value })} placeholder="e.g. 250" disabled={lockedField('employee_count')} />
              </div>
              <div className="space-y-1.5">
                <Label>Year Established</Label>
                <Input type="number" value={form.year_established} onChange={(e) => setForm({ ...form, year_established: e.target.value })} placeholder="e.g. 1990" disabled={lockedField('year_established')} />
              </div>
            </div>
          </div>
          {/* Contact Information */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Contact Information</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.primary_email} onChange={(e) => setForm({ ...form, primary_email: e.target.value })} disabled={lockedField('primary_email')} />
              </div>
              <div className="space-y-1.5">
                <Label>Telephone</Label>
                <Input value={form.primary_phone} onChange={(e) => setForm({ ...form, primary_phone: e.target.value })} disabled={lockedField('primary_phone')} />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Website</Label>
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" disabled={lockedField('website')} />
              </div>
            </div>
          </div>
          {/* Address */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Address</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5 md:col-span-2">
                <Label>Address Line 1</Label>
                <Input value={form.address_line_1} onChange={(e) => setForm({ ...form, address_line_1: e.target.value })} disabled={lockedField('address_line_1')} />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Address Line 2</Label>
                <Input value={form.address_line_2} onChange={(e) => setForm({ ...form, address_line_2: e.target.value })} disabled={lockedField('address_line_2')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="supplier-city">City / Town <span className="text-red-500">*</span></Label>
                <Input id="supplier-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} disabled={lockedField('city')} required />
              </div>
              <div className="space-y-1.5">
                <Label>State / County</Label>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} disabled={lockedField('state')} />
              </div>
              <div className="space-y-1.5">
                <Label>Postal Code</Label>
                <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} disabled={lockedField('postal_code')} />
              </div>
              {/* CR-7774: picked from the shared country list, not typed. */}
              <div className="space-y-1.5">
                <Label htmlFor="supplier-country">Country</Label>
                <SearchableSelect
                  id="supplier-country"
                  options={COUNTRY_OPTIONS}
                  value={form.country}
                  onChange={(v) => setForm({ ...form, country: v })}
                  placeholder="Select country..."
                  disabled={lockedField('country')}
                />
              </div>
            </div>
          </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || addTargetLoading || !effectiveCustomerIds.length || (reuseOnly && !existingSupplierId)}
          >
            {saving
              ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{existingSupplierId ? 'Linking...' : 'Adding...'}</>
              : (existingSupplierId ? 'Link Supplier' : 'Add Supplier')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
